import React, { useState } from 'react';
import {
  AspectRatio,
  Box,
  Button,
  Container,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';

const stats = [
  { value: '15', label: 'behavioral biases' },
  { value: '5', label: 'role workspaces' },
  { value: '30-day', label: 'Solo or Team trial, no card' },
  { value: 'CSV/XLSX', label: 'firm-ready data' },
];

// YouTube is only contacted after a click, in privacy-enhanced mode: no third-party
// request or cookie on page load, so no consent banner is needed for the video.
const WALKTHROUGH_URL = 'https://www.youtube-nocookie.com/embed/HAC5RWngsfI?autoplay=1&playsinline=1&rel=0&controls=1&modestbranding=1';

const HeroSection = () => {
  const [playing, setPlaying] = useState(false);
  return (
    <Box
      as="section"
      className="grid-bg-dark quantum-mesh"
      bg="brand.quantumBg"
      minH={{ base: 'auto', md: '85vh' }}
      py={{ base: 16, md: 24 }}
      overflow="hidden"
    >
      <Container maxW="1500px" position="relative" zIndex={1} px={{ base: 4, md: 8 }}>
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          spacing={{ base: 12, lg: 10, xl: 14 }}
          align="center"
          justify="center"
        >
          <VStack
            flex={{ base: '1', lg: '0 1 56%' }}
            minW={0}
            align={{ base: 'center', lg: 'flex-start' }}
            textAlign={{ base: 'center', lg: 'left' }}
            spacing={7}
          >
            <Text
              display="inline-block"
              bg="rgba(0, 212, 170, 0.1)"
              color="brand.quantumAccent"
              fontSize="xs"
              fontWeight="800"
              letterSpacing="0.15em"
              textTransform="uppercase"
              px={3}
              py={1}
              borderRadius="full"
              maxW="full"
              whiteSpace={{ base: 'normal', md: 'nowrap' }}
            >
              Behavioral intelligence for financial institutions
            </Text>

            <Heading as="h1" fontSize={{ base: '40px', md: '56px', lg: '64px' }} color="white" lineHeight="1.03" maxW="820px">
              Turn your client, product and portfolio data into{' '}
              <Box as="span" className="gradient-text-quantum">
                role-specific work queues
              </Box>
            </Heading>

            <Text fontSize={{ base: 'lg', md: 'xl' }} color="brand.quantumMuted" maxW="720px">
              FinanSee helps banks, wealth managers, and fintech teams turn behavioral
              signals into customer actions and context for human advisory review.
            </Text>

            <Stack
              direction={{ base: 'column', md: 'row' }}
              spacing={4}
              flexWrap="wrap"
              rowGap={3}
              justify={{ base: 'center', lg: 'flex-start' }}
              w={{ base: 'full', md: 'auto' }}
            >
              <Button
                as={Link}
                to="/register"
                size="lg"
                px={8}
                bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                color="brand.quantumBg"
                _hover={{ opacity: 0.9, transform: 'translateY(-1px)' }}
                w={{ base: 'full', md: 'auto' }}
              >
                Try the platform for free
              </Button>
              <Button
                as={Link}
                to="/platform"
                size="lg"
                px={8}
                variant="outline"
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ bg: 'whiteAlpha.100' }}
                w={{ base: 'full', md: 'auto' }}
              >
                Explore features & plans
              </Button>
              <Button
                as="a"
                href="#platform-walkthrough"
                size="lg"
                px={{ base: 8, md: 6, xl: 8 }}
                variant="ghost"
                color="brand.quantumAccent"
                _hover={{ bg: 'whiteAlpha.100', color: 'brand.quantumCyan' }}
                w={{ base: 'full', md: 'auto' }}
              >
                Watch platform presentation
              </Button>
            </Stack>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} w="full" maxW="760px">
              {stats.map((stat) => (
                <Box
                  key={stat.label}
                  bg="rgba(17, 27, 46, 0.78)"
                  borderWidth="1px"
                  borderColor="brand.quantumBorder"
                  borderRadius="2xl"
                  px={4}
                  py={4}
                  boxShadow="0 18px 44px rgba(0, 0, 0, 0.22)"
                  textAlign="center"
                >
                  <Text color="white" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800">
                    {stat.value}
                  </Text>
                  <Text color="brand.quantumMuted" fontSize="sm">
                    {stat.label}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>

          <Box
            id="platform-walkthrough"
            flex={{ base: '1', lg: '0 1 44%' }}
            w="full"
            maxW={{ base: 'full', lg: '620px', xl: '680px' }}
            bg="rgba(17, 27, 46, 0.86)"
            borderWidth="1px"
            borderColor="brand.quantumBorder"
            borderRadius="2xl"
            p={{ base: 4, md: 6 }}
            boxShadow="0 24px 80px rgba(0, 0, 0, 0.38)"
            backdropFilter="blur(12px)"
          >
            <VStack spacing={4} align="stretch">
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="800"
                  color="brand.quantumAccent"
                  textTransform="uppercase"
                  letterSpacing="0.15em"
                  mb={2}
                >
                  Platform presentation
                </Text>
                <Heading size="md" color="white">
                  See how FinanSee queues next actions for each operating role.
                </Heading>
              </Box>

              <Box
                borderRadius="16px"
                overflow="hidden"
                bg="#050816"
                borderWidth="1px"
                borderColor="whiteAlpha.200"
                position="relative"
              >
                <AspectRatio ratio={16 / 9} w="full">
                  {playing ? (
                    <iframe
                      title="FinanSee platform walkthrough"
                      src={WALKTHROUGH_URL}
                      allow="autoplay; encrypted-media; picture-in-picture; web-share"
                      allowFullScreen
                      style={{ width: '100%', height: '100%', border: 0, background: '#0f172a' }}
                    />
                  ) : (
                    <VStack
                      justify="center"
                      spacing={3}
                      bgGradient="radial(circle at 50% 40%, #13315C, #050816)"
                    >
                      <Button
                        size="lg"
                        borderRadius="full"
                        bg="brand.quantumAccent"
                        color="#050816"
                        _hover={{ opacity: 0.9 }}
                        onClick={() => setPlaying(true)}
                        aria-label="Play the FinanSee platform walkthrough video"
                      >
                        ▶ Watch the 2-minute walkthrough
                      </Button>
                      <Text fontSize="xs" color="whiteAlpha.700">
                        Plays from YouTube in privacy-enhanced mode
                      </Text>
                    </VStack>
                  )}
                </AspectRatio>
              </Box>
            </VStack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default HeroSection;
